package ai.wagemix.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initPrivacy_policyPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("privacy_policy.md", FrontMatterElement.Builder { addScalar("title", "Privacy Policy"); }))
}

@Page("/privacy-policy")
@Layout("ai.wagemix.components.layouts.MarkdownLayout")
@Composable
fun Privacy_policyPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("privacy-policy-for-wagemix") }) {
        org.jetbrains.compose.web.dom.Text("Privacy Policy for WageMix")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Last Updated:")
        }
        org.jetbrains.compose.web.dom.Text(" Nov. 1, 2026")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("1-introduction") }) {
        org.jetbrains.compose.web.dom.Text("1. Introduction")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Welcome to WageMix (\"we,\" \"our,\" or \"us\"). We provide AI-driven wage analysis and occupational insights. We are committed to protecting your privacy and handling your personal information with care and transparency. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and services.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("2-information-we-collect") }) {
        org.jetbrains.compose.web.dom.Text("2. Information We Collect")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("2-1-personal-information") }) {
        org.jetbrains.compose.web.dom.Text("2.1 Personal Information")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you register for an account, we may collect:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Identity Data:")
            }
            org.jetbrains.compose.web.dom.Text(" Name (first and last) and email address.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Note:")
            }
            org.jetbrains.compose.web.dom.Text(" Passwords are managed securely by ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Google Firebase Authentication")
            }
            org.jetbrains.compose.web.dom.Text(". We do not store or have access to your password.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you create a share link, the shared page shows your first name and the initial of your last name to anyone who opens the link (see section 2.5).")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("2-2-usage-and-technical-data") }) {
        org.jetbrains.compose.web.dom.Text("2.2 Usage and Technical Data")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We automatically collect information when you interact with WageMix:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Browser type, version, and IP address.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Pages visited, features used, and time/date of visits.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("AI Interaction Data:")
            }
            org.jetbrains.compose.web.dom.Text(" Occupation searches, analysis history, and specific prompts or data points provided to our AI tools for processing.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Usage analytics are linked to your account.")
        }
        org.jetbrains.compose.web.dom.Text(" When you are signed in, the usage events your browser sends (for example, the screens you open, the features you use, and the text you type into the occupation search) are linked to your account. Your analytics profile holds your email address, your name, your plan, whether your email is verified, and when you signed up. We keep your email there so we can contact you if something goes wrong with your account or your data. When you are not signed in, events are grouped by a random identifier for your device and are not linked to an account.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Please do not type personal information into the occupation search; the search field reminds you of this. Our analytics system runs on our own hosting. It is not a third-party analytics or advertising service, and access to it is limited to the people who would contact you about a problem.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("2-3-payment-information") }) {
        org.jetbrains.compose.web.dom.Text("2.3 Payment Information")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you subscribe to paid services:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Payment information is processed by ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Stripe")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("We do not store complete credit card information on our servers; we retain only transaction history and subscription status.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("2-4-uploaded-job-descriptions") }) {
        org.jetbrains.compose.web.dom.Text("2.4 Uploaded Job Descriptions")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you upload a job description, either as a file or as the address (URL) of a job posting, we:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Fetch the posting:")
            }
            org.jetbrains.compose.web.dom.Text(" for a URL, our rendering service on our hosting opens the page and saves it as a PDF.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Scan it for malware")
            }
            org.jetbrains.compose.web.dom.Text(" on our hosting before anything is stored.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Store it")
            }
            org.jetbrains.compose.web.dom.Text(" in object storage with our hosting provider, so you can view and download it later.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Send it to OpenAI for analysis")
            }
            org.jetbrains.compose.web.dom.Text(", to check that it is a job description and to suggest an occupation mix (see section 3).")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If an upload is rejected (because it is not a job description, or because it contains malware or content meant to manipulate the analysis), we delete the file from our storage and from OpenAI, and then delete its record. You can also delete an upload that failed.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("We do not check uploads for personal information.")
        }
        org.jetbrains.compose.web.dom.Text(" We do not inspect uploaded files or fetched pages for names, contact details, or other personal information. You are responsible for making sure a file or page you upload contains no personal information, yours or anyone else's. Anything you upload is processed as uploaded.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("2-5-share-links") }) {
        org.jetbrains.compose.web.dom.Text("2.5 Share Links")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you share a mix, we create a public link. Anyone who has the link can open the shared page, which shows the mix and your first name and last initial. We count how often each link is opened; we do not record who opened it. Pro subscribers can see these counts for their own links. While you have a paid plan, you can turn a link off at any time, and it then shows that it has been deactivated.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("3-ai-data-usage") }) {
        org.jetbrains.compose.web.dom.Text("3. AI Data Usage")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("As an AI-driven platform, we process data to provide intelligent insights. This is how your data is used by our AI features:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Uploaded Job Descriptions:")
            }
            org.jetbrains.compose.web.dom.Text(" We send the job descriptions you upload to ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("OpenAI")
            }
            org.jetbrains.compose.web.dom.Text(" to check that each is a job description and to suggest an occupation mix for it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Account Data in Training:")
            }
            org.jetbrains.compose.web.dom.Text(" We do not use the name or email address of your account to train machine learning models. Because we do not check uploads for personal information (section 2.4), we cannot promise that an upload contains none; whatever you upload is processed as you uploaded it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Input Privacy:")
            }
            org.jetbrains.compose.web.dom.Text(" Any professional data you input for analysis is processed to provide your specific result.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("4-data-sources-and-third-party-services") }) {
        org.jetbrains.compose.web.dom.Text("4. Data Sources and Third-Party Services")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("4-1-data-sources") }) {
        org.jetbrains.compose.web.dom.Text("4.1 Data Sources")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("WageMix utilizes the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("U.S. Bureau of Labor Statistics (BLS)")
        }
        org.jetbrains.compose.web.dom.Text(" Occupational Employment and Wage Statistics (OEWS) dataset. This is public data and does not contain PII.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("4-2-third-party-processors") }) {
        org.jetbrains.compose.web.dom.Text("4.2 Third-Party Processors")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We partner with several providers to deliver our service:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Authentication:")
            }
            org.jetbrains.compose.web.dom.Text(" Google Firebase (governed by Google’s Privacy Policy).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Payments:")
            }
            org.jetbrains.compose.web.dom.Text(" Stripe, Inc. (governed by Stripe’s Privacy Policy).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Email:")
            }
            org.jetbrains.compose.web.dom.Text(" Proton Mail, which sends our account emails (welcome and email verification, and password reset).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Hosting:")
            }
            org.jetbrains.compose.web.dom.Text(" Akamai Connected Cloud (Linode) and other infrastructure providers. Our usage analytics and our malware scanning run on this hosting.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("AI Infrastructure:")
            }
            org.jetbrains.compose.web.dom.Text(" OpenAI, which analyzes the job descriptions you upload (section 2.4).")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("5-cookies-and-tracking-technologies") }) {
        org.jetbrains.compose.web.dom.Text("5. Cookies and Tracking Technologies")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We use cookies and similar technologies, such as your browser's local storage, to keep you signed in, remember your preferences, and collect the usage analytics described in section 2.2: the screens you open, the features you use, and your occupation searches, linked to your account when you are signed in. Our analytics run on our own hosting; we do not use third-party advertising or analytics networks. You can control cookie settings through your browser, but disabling them may limit certain features of the application.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("6-data-security") }) {
        org.jetbrains.compose.web.dom.Text("6. Data Security")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We implement industry-standard security measures to protect your information, including:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Encryption of data in transit (HTTPS/TLS).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Regular security updates and access controls.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Secure authentication via Firebase to ensure we never handle or store your raw password data.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("7-your-rights-and-choices") }) {
        org.jetbrains.compose.web.dom.Text("7. Your Rights and Choices")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Depending on your jurisdiction (such as the GDPR or CCPA/CPRA), you may have the following rights:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Access & Portability:")
            }
            org.jetbrains.compose.web.dom.Text(" Request a copy of the personal data we hold about you.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Correction:")
            }
            org.jetbrains.compose.web.dom.Text(" Request updates to inaccurate or incomplete information.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Deletion:")
            }
            org.jetbrains.compose.web.dom.Text(" Request that we erase your personal data, subject to legal retention requirements. To delete your account, email ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("privacy@wagemix.ai")
            }
            org.jetbrains.compose.web.dom.Text(" from the address on your account. Deleting your account also deletes your analytics profile.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("California Privacy:")
            }
            org.jetbrains.compose.web.dom.Text(" We do not \"sell\" your personal information. California residents may request a notice of categories of data collected and opt-out of \"sharing\" for cross-contextual advertising.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To exercise these rights, please contact us at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("privacy@wagemix.ai")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("8-data-retention") }) {
        org.jetbrains.compose.web.dom.Text("8. Data Retention")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We retain your information for as long as your account is active or as needed to provide services. When data is no longer required for business or legal purposes, we securely delete or anonymize it.")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("If your subscription ends:")
            }
            org.jetbrains.compose.web.dom.Text(" we keep your saved mixes, uploads, and share links, but you cannot see or use them until you subscribe again; they are restored when you do. While your subscription has ended, your share links are deactivated for everyone who opens them.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Rejected uploads:")
            }
            org.jetbrains.compose.web.dom.Text(" an upload that is rejected (section 2.4) is deleted, together with its record.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("9-children-s-privacy") }) {
        org.jetbrains.compose.web.dom.Text("9. Children's Privacy")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("WageMix is not intended for individuals under the age of 18. We do not knowingly collect information from children. If we become aware of such collection, we will take immediate steps to delete the data.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("10-legal-basis-for-processing-gdpr-eea-users") }) {
        org.jetbrains.compose.web.dom.Text("10. Legal Basis for Processing (GDPR/EEA Users)")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For users in the European Economic Area, our legal bases include:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Contract:")
            }
            org.jetbrains.compose.web.dom.Text(" Processing necessary to provide the WageMix service.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Consent:")
            }
            org.jetbrains.compose.web.dom.Text(" Where you have given explicit permission for specific data use.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Legitimate Interests:")
            }
            org.jetbrains.compose.web.dom.Text(" For platform security and fraud prevention.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("11-contact-us") }) {
        org.jetbrains.compose.web.dom.Text("11. Contact Us")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For questions, concerns, or data requests regarding this Privacy Policy, please contact us at:")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Email:")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("mailto:privacy@wagemix.ai") {
            org.jetbrains.compose.web.dom.Text("privacy@wagemix.ai")
        }
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Company:")
        }
        org.jetbrains.compose.web.dom.Text(" WageMix")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Address:")
        }
        org.jetbrains.compose.web.dom.Text(" 501 17th Street S.W.")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("Jamestown, ND 58401-6215")
    }
}
